/**
 * Search's pure half: the result union, how a group is ranked and cut, where
 * each result leads, and the recents list. No React, no Query, no messages —
 * the hook (`use-search-results.ts`) hands in strings already localized.
 *
 * The matcher itself (`tokenize`, `score`, `highlight`) is `@repo/client/search`
 * (T23), A2's verbatim; nothing here re-implements a piece of it.
 */
import { type SearchField, score } from "@repo/client/search";
import type { ExerciseListItem } from "@repo/contract/catalog";
import type {
  PlanItemView,
  PlanView,
  WorkoutSessionView,
} from "@repo/contract/training";
import { closeFallbackFor } from "../../app/layers";
import type { Search } from "../../app/search";
import type { SectionId } from "../profile/sections/section-meta";

/** What an action result does. `continue` replaces `start` while a session runs. */
export type ActionId = "start" | "continue" | "draft" | "weight" | "wrapped";

interface Base {
  /** Unique across every group: it is the keyboard cursor's identity. */
  readonly id: string;
  readonly label: string;
  readonly meta: string;
}

export interface HistoryEntry {
  /** Ended as abandoned (`D177`): kept in the history, and marked. */
  readonly abandoned: boolean;
  readonly ended: boolean;
  readonly exercises: number;
  readonly letter: string;
  readonly minutes: number;
  readonly sessionId: string;
  readonly sets: number;
  readonly startedAt: string;
}

export type Result =
  | (Base & {
      readonly kind: "exercise";
      readonly exercise: ExerciseListItem;
      /** "Treino A" when the plan holds it, else null. */
      readonly plan: string | null;
    })
  | (Base & {
      readonly kind: "plan";
      readonly item: PlanItemView;
      readonly session: string;
    })
  | (Base & {
      readonly kind: "history";
      /** "12 set", for the preview's eyebrow. */
      readonly date: string;
      readonly entry: HistoryEntry;
    })
  | (Base & {
      readonly kind: "conversation";
      readonly conversationId: string;
      readonly when: string;
    })
  | (Base & {
      readonly kind: "setting";
      readonly section: SectionId;
      /** Just the stored value, for the preview (the meta also names the section). */
      readonly value: string;
    })
  | (Base & { readonly kind: "action"; readonly action: ActionId })
  | (Base & { readonly kind: "recent" })
  | (Base & { readonly kind: "ask" });

export type ResultKind = Result["kind"];

export type GroupId =
  | "recent"
  | "exercises"
  | "plan"
  | "history"
  | "conversations"
  | "settings"
  | "actions";

export interface Group {
  readonly id: GroupId;
  /** How many matched before the group was cut short. */
  readonly more: boolean;
  readonly results: readonly Result[];
  readonly title: string;
  readonly total: number;
}

/** A local result and the fields it is found by. */
export interface Indexed {
  readonly fields: readonly SearchField[];
  readonly result: Result;
}

/** How many rows each local group shows (A2 `search-index.ts`). */
export const GROUP_LIMIT = {
  actions: 4,
  conversations: 4,
  exercises: 5,
  history: 4,
  plan: 5,
  settings: 5,
} as const;

/** The exercise page asked of the server (T66 spec: `limit=20`). */
export const EXERCISE_PAGE = 20;
/** Debounce before the server is asked (T66 spec). */
export const EXERCISE_DEBOUNCE_MS = 200;
/** Recents kept on the device. */
export const RECENTS_MAX = 5;
/** Muscle chips offered while browsing: the catalogue's biggest. */
export const BROWSE_MUSCLES = 10;

/**
 * The entries every token hits, best first; ties keep their input order
 * (`Array.prototype.sort` is stable). No token ranks nothing.
 */
export function rankIndexed(
  entries: readonly Indexed[],
  tokens: readonly string[]
): Result[] {
  if (tokens.length === 0) {
    return [];
  }
  const hits: { result: Result; score: number }[] = [];
  for (const entry of entries) {
    const s = score(entry.fields, tokens);
    if (s !== null) {
      hits.push({ result: entry.result, score: s });
    }
  }
  return hits.sort((a, b) => b.score - a.score).map((h) => h.result);
}

/** A group of `results` cut to `limit`, or nothing when empty. */
export function groupOf(
  id: GroupId,
  title: string,
  results: readonly Result[],
  limit: number
): Group | null {
  if (results.length === 0) {
    return null;
  }
  return {
    id,
    more: results.length > limit,
    results: results.slice(0, limit),
    title,
    total: results.length,
  };
}

/** Every row in display order, then the ask card — the keyboard's list. */
export function flatten(
  groups: readonly Group[],
  ask: Result | null
): Result[] {
  const flat = groups.flatMap((g) => [...g.results]);
  return ask ? [...flat, ask] : flat;
}

/** The cursor kept inside the list (`-1`-safe for an empty list). */
export function clampCursor(cursor: number, length: number): number {
  return Math.max(0, Math.min(length - 1, cursor));
}

/** The catalogue exercise ids a plan holds, with the session each sits in. */
export function planExerciseSessions(
  plan: PlanView | null | undefined
): Map<string, string> {
  const out = new Map<string, string>();
  for (const session of plan?.sessions ?? []) {
    for (const item of session.items) {
      if (!out.has(item.exerciseId)) {
        out.set(item.exerciseId, session.label);
      }
    }
  }
  return out;
}

/**
 * A session's numbers for its row: minutes from start to end (0 while it
 * runs), sets, distinct exercises, and the plan session's letter when the
 * plan still has that session.
 */
export function historyEntryOf(
  session: WorkoutSessionView,
  planLabels: ReadonlyMap<string, string>
): HistoryEntry {
  const start = Date.parse(session.startedAt);
  const end = session.endedAt === null ? start : Date.parse(session.endedAt);
  const letter =
    session.planSessionId === null
      ? ""
      : (planLabels.get(session.planSessionId) ?? "");
  return {
    abandoned: session.abandoned === true,
    ended: session.endedAt !== null,
    exercises: new Set(session.sets.map((s) => s.exerciseId)).size,
    letter,
    minutes: Math.max(0, Math.round((end - start) / 60_000)),
    sessionId: session.sessionId,
    sets: session.sets.length,
    startedAt: session.startedAt,
  };
}

/**
 * A history row's meta line. An abandoned session is not a workout
 * (`D177`), but its sets were real, so it stays in the history — marked, so
 * it never reads as an ordinary ended workout. `mark` is the localized
 * message itself, in its own shape: the hook passes `search.history_abandoned`
 * by name, with no wrapper of its own between the flag and the word, because
 * no test reaches the hook and a wrapper there could return the line unmarked
 * with every test green.
 */
export function historyMetaOf(
  entry: HistoryEntry,
  plain: string,
  mark: (inputs: { readonly meta: string }) => string
): string {
  return entry.abandoned ? mark({ meta: plain }) : plain;
}

/** Newest first, without mutating the cached array. */
export function newestFirst(
  sessions: readonly WorkoutSessionView[]
): WorkoutSessionView[] {
  return [...sessions].sort(
    (a, b) => Date.parse(b.startedAt) - Date.parse(a.startedAt)
  );
}

/** `query` at the front, case-insensitively unique, at most `RECENTS_MAX`. */
export function remember(recents: readonly string[], query: string): string[] {
  const q = query.trim();
  if (q.length < 2) {
    return [...recents];
  }
  const lower = q.toLowerCase();
  return [q, ...recents.filter((r) => r.toLowerCase() !== lower)].slice(
    0,
    RECENTS_MAX
  );
}

const ACTION_PARAMS: Readonly<Record<ActionId, Partial<Search>>> = {
  continue: { screen: "workout" },
  draft: { sheet: "draft" },
  start: { screen: "workout", start: "1" },
  weight: { edit: "weight", overlay: "profile", section: "body" },
  wrapped: { overlay: "wrapped" },
};

/**
 * Where a result leads, as the params that REPLACE the search layer (A2
 * `go()`, :110-116). `null` for a recent, which only refills the field.
 * `query` is the trimmed text, for the ask card and "see all".
 */
export function destinationOf(
  result: Result,
  query: string
): Partial<Search> | null {
  switch (result.kind) {
    case "exercise":
      return { ex: result.exercise.id, sheet: "exercise" };
    // The exercise sheet on the plan (`D181`), for this exercise on the day
    // the result named — `ex` is an exercise id, and one exercise may be on
    // two days.
    case "plan":
      return {
        ex: result.item.exerciseId,
        screen: "plan",
        session: result.item.sessionId,
        sheet: "exercise",
      };
    case "history":
      return result.entry.ended
        ? { session: result.entry.sessionId, sheet: "summary" }
        : { screen: "workout" };
    case "conversation":
      return { conversation: result.conversationId, overlay: "coach" };
    case "setting":
      return { overlay: "profile", section: result.section };
    case "action":
      return ACTION_PARAMS[result.action];
    case "ask":
      return { ask: query, overlay: "coach" };
    default:
      return null;
  }
}

/** "Ver todos": the virtualised catalogue, handed the query (T67 reads `q`). */
export function seeAllDestination(query: string): Partial<Search> {
  return query === ""
    ? { sheet: "catalogue" }
    : { q: query, sheet: "catalogue" };
}

/**
 * The whole search to REPLACE with: the search overlay's params cleared, and
 * — unless the destination is the coach, which coexists with a sheet (D04) —
 * any sheet under search cleared too, so a profile never opens beneath a
 * stale sheet. Then the destination on top.
 */
export function replacementFor(dest: Partial<Search>): Partial<Search> {
  const keepSheet = dest.overlay === "coach" && dest.sheet === undefined;
  return {
    ...closeFallbackFor("overlay"),
    ...(keepSheet ? {} : closeFallbackFor("sheet")),
    ...dest,
  };
}
